import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Backdrop,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FlagIcon from "@mui/icons-material/Flag";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import moment from "moment";
import TextButtonComponet from "../../../component/atom/Buttons/TextButton";
import { useLocation, useNavigate } from "react-router-dom";
import HeadingComponent from "../../../component/atom/Headings/Heading";
import QuizAssesmentCard from "../../../component/molecule/Cards/QuizAssesmentCard";
import QuizMistakeLogCard from "../../../component/molecule/Cards/QuizMistakeLogCard";
import {
  AssementButton,
  CircularWithValueLabel,
  TextEditior,
} from "../../../component/atom";
import { connect, useDispatch } from "react-redux";
import { Actions } from "../../../core/modules/Actions";
import TextAreaComponent from "../../../component/atom/Inputs/TextArea";
import { USER_ROLE } from "../../../core/Constant";
import AssessmentQuestionList from "../../../component/molecule/Cards/AssessmentQuestionList";
import { formatLocalDateTime } from "../../../core/dateTimeUtils";

const isAssessmentQuestionFlagged = (q) =>
  q?.is_flag === 1 ;

const formatAssessmentDateTime = (dateValue) => {
  return formatLocalDateTime(dateValue, "Do MMMM YYYY HH:mm:ss", true);
};

const ASSESSMENT_DISCLAIMER_TEXT =
  "As this quiz was recently updated, you may notice some changes in the questions compared to your previous attempt. Your percentage has also been recalculated according to the latest version of the questionnaire.";

const ASSESSMENT_DISCLAIMER_TEXT_NOTE = "Feel free to attempt the updated quiz again and check your revised performance.";

const shouldShowAssessmentDisclaimer = (assessmentInfo, userRole) => {
  if (userRole !== USER_ROLE.student) {
    return false;
  }

  const updatedAt = moment(assessmentInfo?.updated_at);
  const attemptedAt = moment(assessmentInfo?.attempted_at);

  if (!updatedAt.isValid() || !attemptedAt.isValid()) {
    return false;
  }

  return updatedAt.isAfter(attemptedAt);
};

const QuizAttemptFormScreen = ({
  getAssessmentDetails,
  assessmentDetails,
  getAssessmentQnR,
  assessmentQnR,
  getAssessmentQnRAdmin,
  mistakeLogDetails,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const quizStatusId = location?.state?.quiz_status_id;
  const quizId = location?.state?.quiz_id;
  const userId = location?.state?.user_id;
  const quizName = location?.state?.quiz_name;
  const studentName = location?.state?.stuName;
  const attempt = location?.state?.attempt;
  const attemptHistory = Array.isArray(location?.state?.attempts)
    ? [...location.state.attempts].sort(
        (first, second) => Number(second?.attempts || 0) - Number(first?.attempts || 0)
      )
    : [];
  const [assessmentInfo, setAssessmentInfo] = useState({});
  const [feedback, setFeedback] = useState("");
  const [errorStatus, setErrorStatus] = useState(false);
  const [userRole, setUserRole] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [questionDetails, setQuestionDetails] = useState(null);
  const [showClusterQuestions, setShowClusterQuestions] = useState(false);
  const [createdMistakeLogIds, setCreatedMistakeLogIds] = useState({});

  useEffect(() => {
    setUserRole(parseInt(localStorage.getItem("userType")));
    setAssessmentInfo(assessmentDetails);
    setFeedback(assessmentDetails?.feedback);
  }, [assessmentDetails]);
  useEffect(() => {}, [feedback]);

  useEffect(() => {
    getAssessmentDetails({
      status_id: quizStatusId,
      user_id: userId,
    });
    dispatch(Actions.quizes.getMistakeLog(quizStatusId));
  }, [dispatch, getAssessmentDetails, quizStatusId, userId]);

  useEffect(() => {
    if (assessmentQnR) {
      setQuestionDetails(assessmentQnR);
    }
  }, [assessmentQnR]);

  const fetchedMistakeLogs = mistakeLogDetails?.mistake_logs || [];
  const questions = assessmentInfo?.questions || [];
  const showAssessmentDisclaimer = shouldShowAssessmentDisclaimer(
    assessmentInfo,
    userRole
  );
  const showAssessmentDates = showAssessmentDisclaimer;

  useEffect(() => {
    if (currentQuestionId) {
      const payload = {
        quiz_id: quizId,
        question_id: currentQuestionId,
        attempt: attempt,
      };
      if (userRole === USER_ROLE.student) {
        getAssessmentQnR(payload);
      } else {
        getAssessmentQnRAdmin({
          ...payload,
          user_id: userId,
        });
      }
    }
  }, [
    currentQuestionId,
    userRole,
    getAssessmentQnR,
    getAssessmentQnRAdmin,
    quizId,
    attempt,
    userId,
  ]);

  const dotBtnClick = (value, index) => {
    setSelectedQuestionIndex(index);
    setCurrentQuestionId(value?.id);
    setDetailsOpen(true);
  };

  const handlePrevQuestion = () => {
    const newIndex = (selectedQuestionIndex || 0) - 1;
    if (newIndex >= 0 && questions[newIndex]) {
      setSelectedQuestionIndex(newIndex);
      setCurrentQuestionId(questions[newIndex].id);
    }
  };

  const handleNextQuestion = () => {
    const newIndex = (selectedQuestionIndex || 0) + 1;
    if (newIndex < questions.length && questions[newIndex]) {
      setSelectedQuestionIndex(newIndex);
      setCurrentQuestionId(questions[newIndex].id);
    }
  };
  const showPrev =
    selectedQuestionIndex !== null && selectedQuestionIndex > 0;
  const showNext =
    selectedQuestionIndex !== null &&
    questions?.length > 0 &&
    selectedQuestionIndex < questions.length - 1;
  const currentQuestionLabel =
    selectedQuestionIndex !== null ? selectedQuestionIndex + 1 : 1;
  const handleSubmit = () => {
    if (!errorStatus) {
      setIsSubmitting(true);
      dispatch(
        Actions.quizes.addFeedbackForm({
          quiz_status_id: quizStatusId,
          feedback: feedback,
        })
      );
      setFeedback("");
      setTimeout(() => {
        getAssessmentDetails({
          status_id: quizStatusId,
          user_id: userId,
        });
        setFeedback(assessmentDetails?.feedback);
        setIsSubmitting(false);
      }, 2000);
    }
  };

  const onFeedbackChange = (value) => {
    setFeedback(value);
    if (value === undefined || value === null || value === "") {
      setErrorStatus(true);
    } else {
      setErrorStatus(false);
    }
  };
  return (
    <>
      <Box className="main-screen-container">
        <Box mt={1}>
          <HeadingComponent
            text={"Quiz 0" + quizId}
            backNavigation={true}
            fontweigth={600}
            size={26}
            fontfamily={"Montserrat"}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            p: 2,
            mt: 0.2,
          }}
        >
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    marginRight: 2,
                    height: "100%",
                  }}
                >
                  <span className="quiz-card-txt">{quizName}</span>
                  <span
                    style={{
                      fontFamily: "Montserrat",
                      fontWeight: 600,
                      color: "#9C9C9C",
                      fontSize: "14px",
                    }}
                  >
                    Attempt {attempt < 10 ? "0" + attempt?.toString() : attempt}
                  </span>
                </Box>
                {userRole && !detailsOpen ? (
                  <CircularWithValueLabel
                    color="#28B882"
                    progress="Done"
                    text="Done"
                  />
                ) : null}
              </Box>
              <p
                style={{ width: 200, textAlign: "center" }}
                className="quiz-attempt-marktext"
              >
                {userRole ? "Marks" : "Percentage"} - {assessmentInfo?.marks}%
              </p>
            </Box>
            {!userRole ? (
              <Box mt={1}>
                <p className="quiz-card-txt">{studentName}</p>
              </Box>
            ) : null}
          </Box>

          {showAssessmentDates ? (
            <Box className="quiz-assessment-dates-card">
              <Box className="quiz-assessment-date-item">
                <CalendarTodayOutlinedIcon className="quiz-assessment-date-icon" />
                <Box>
                  <p className="quiz-assessment-date-label">Quiz created at</p>
                  <p className="quiz-assessment-date-value">
                    {formatAssessmentDateTime(assessmentInfo?.created_at)}
                  </p>
                </Box>
              </Box>
              <Box className="quiz-assessment-date-item">
                <EditOutlinedIcon className="quiz-assessment-date-icon" />
                <Box>
                  <p className="quiz-assessment-date-label">You attempted at</p>
                  <p className="quiz-assessment-date-value">
                    {formatAssessmentDateTime(assessmentInfo?.attempted_at)}
                  </p>
                </Box>
              </Box>
              <Box className="quiz-assessment-date-item">
                <AutorenewOutlinedIcon className="quiz-assessment-date-icon" />
                <Box>
                  <p className="quiz-assessment-date-label">Quiz updated at</p>
                  <p className="quiz-assessment-date-value">
                    {formatAssessmentDateTime(assessmentInfo?.updated_at)}
                  </p>
                </Box>
              </Box>
            </Box>
          ) : null}

          {showAssessmentDisclaimer ? (
            <Box className="quiz-assessment-disclaimer">
              <InfoOutlinedIcon className="quiz-assessment-disclaimer-icon" />
              <p className="quiz-assessment-disclaimer-text">
                <strong>Disclaimer:</strong> {ASSESSMENT_DISCLAIMER_TEXT}<br/><br/>{ASSESSMENT_DISCLAIMER_TEXT_NOTE}
              </p>
            </Box>
          ) : null}

          {/* Inline Container for Note and question view toggle */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 1,
                      }}
                    >
                    <span style={{ fontSize: "14px", fontWeight: "bold" }}>
                      Note:Please click on the question number to view the answer
                    </span>
                    <TextButtonComponet
                      style={{ width: 280 }}
                      text={showClusterQuestions ? "View All Questions" : "View Cluster Wise Question"}
                      onButtonClick={() => setShowClusterQuestions((previous) => !previous)}
                    />
                  </Box>

                  <Box>
                    <AssessmentQuestionList
                      questions={questions}
                      clusterQuestions={assessmentInfo?.cluster_wise_questions}
                      showClusters={showClusterQuestions}
                      userRole={userRole}
                      studentRole={USER_ROLE.student}
                      onQuestionClick={dotBtnClick}
                      isFlagged={isAssessmentQuestionFlagged}
                    />
          </Box>

          {/* Review Answers Button */}
                {userRole === USER_ROLE.student && (
                <Box mb={2} mt={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Click below to review all correct answers
                  </Typography>
                   <TextButtonComponet
                   style={{ width: 180 }}
                     text="Review Answers"
                     onButtonClick={() => {
                       // all question
                       const questionIds = questions.map(q => q.id);
                       navigate("/quiz-review-answers", {
                         state: {
                           quizId,
                           attempt,
                           userId,
                           questionIds
                         }
                       });
                     }}
                   />
                </Box>
          )}
          <Box>
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 700,
                fontSize: "18px",
                color: "#1E293B",
                mt: 3,
                mb: 3
            }}>Feedback</Typography>
            {userRole ? (
              <>
                {assessmentInfo?.feedback ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: assessmentInfo?.feedback,
                    }}
                  />
                ) : (
                  "Feedback not given"
                )}
              </>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

                <TextEditior
                  value={feedback}
                  isError={errorStatus}
                  error="Please enter feedback before submitting"
                  placeholder={"Enter your feedback here.."}
                  onchange={onFeedbackChange}
                />

                <Grid container spacing={1} justifyContent={"flex-end"}>
                  <Grid item>
                    <TextButtonComponet
                      text={"Add"}
                      onButtonClick={handleSubmit}
                    />
                  </Grid>
                  <Grid item>
                    <TextButtonComponet
                      classStyle="btn btn-secondary"
                      onButtonClick={() => {
                        setFeedback("");
                        setErrorStatus(false);
                      }}
                      text={"Cancel"}
                    />
                  </Grid>
                  <Backdrop
                    open={isSubmitting}
                    style={{ zIndex: 9999, color: "#fff" }}
                  >
                    <CircularProgress color="inherit" />
                    <span style={{ marginLeft: 16 }}>Submitting...</span>
                  </Backdrop>
                </Grid>
              </Box>
            )}
          </Box>

          {userRole === USER_ROLE.student && (
            <>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 700,
              fontSize: "18px",
              color: "#1E293B",
              mt: 3,
              mb: -2,
            }}
          >
            Mistake Log
          </Typography>
          {(attemptHistory.length > 0
            ? attemptHistory
            : [{
                id: quizStatusId,
                attempts: attempt,
                finished_at: assessmentInfo?.attempted_at,
                mistake_log: assessmentInfo?.mistake_log,
              }]
          ).map((attemptInfo, index) => {
            const isSelectedAttempt = String(attemptInfo?.id) === String(quizStatusId);
            const mistakeLogRecord = fetchedMistakeLogs.find(
              (log) => String(log?.quiz_status_id) === String(attemptInfo?.id)
            );
            const mistakeLogId = mistakeLogRecord?.id ?? createdMistakeLogIds[attemptInfo?.id];
            const mistakeLog = mistakeLogRecord?.title ?? (isSelectedAttempt
              ? assessmentInfo?.mistake_log ?? attemptInfo?.mistake_log ?? ""
              : attemptInfo?.mistake_log || "");

            return (
              <QuizMistakeLogCard
                key={attemptInfo?.id ?? attemptInfo?.attempts ?? index}
                attemptNumber={attemptInfo?.attempts ?? index + 1}
                completedAt={formatAssessmentDateTime(
                  attemptInfo?.finished_at ||
                    (isSelectedAttempt ? assessmentInfo?.attempted_at : null)
                )}
                initialLog={mistakeLog}
                onSaveLog={(logContent) => {
                  if (mistakeLogId) {
                    return dispatch(
                      Actions.quizes.updateMistakeLog({
                        quizMistakeLogId: mistakeLogId,
                        title: logContent,
                      })
                    );
                  }
                  return dispatch(
                    Actions.quizes.saveMistakeLog({
                      title: logContent,
                      quiz_status_id: attemptInfo?.id ?? quizStatusId,
                    })
                  ).then((response) => {
                    const createdLogId =
                      response?.data?.mistake_log?.id ?? response?.mistake_log?.id;
                    if (createdLogId) {
                      setCreatedMistakeLogIds((currentIds) => ({
                        ...currentIds,
                        [attemptInfo?.id ?? quizStatusId]: createdLogId,
                      }));
                    }
                    return response;
                  });
                }}
              />
            );
          })}
            </>
          )}
        </Box>
        <Dialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <Box p={3} sx={{ position: "relative" }}>
            <IconButton
              onClick={() => setDetailsOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8, color: "#111" }}
            >
              <CloseIcon fontSize="small" sx={{ position: 'fixed'}}/>
            </IconButton>
            <Box mb={2}>
              <HeadingComponent
                text={quizId < 10 ? "Quiz 0" + quizId : "Quiz " + quizId}
                fontweigth={600}
                size={22}
                fontfamily={"Montserrat"}
              />
              <p className="quiz-card-txt">{quizName}</p>
              <p
                style={{
                  fontFamily: "Montserrat",
                  color: "#9c9c9c",
                  marginTop: 4,
                }}
              >
                Attempt {attempt}
              </p>
            </Box>
            <QuizAssesmentCard
              index={selectedQuestionIndex}
              data={questionDetails}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 3,
              }}
            >
              <Box>
                {showPrev && (
                  <TextButtonComponet
                    text={`← Prev (Q${currentQuestionLabel - 1})`}
                    onButtonClick={handlePrevQuestion}
                    classStyle="btn btn-secondary"
                  />
                )}
              </Box>
              <Box>
                {showNext && (
                  <TextButtonComponet
                    text={`Next (Q${currentQuestionLabel + 1}) →`}
                    onButtonClick={handleNextQuestion}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Dialog>
      </Box>
    </>
  );
};

export default connect(
  (state) => ({
    assessmentDetails: state.quizes.get("assessmentDetails"),
    assessmentQnR: state.quizes.get("assessmentFormQnR"),
    mistakeLogDetails: state.quizes.get("mistakeLogDetails"),
  }),
  {
    getAssessmentDetails: Actions.quizes.getAssessmentFormDetails,
    getAssessmentQnR: Actions.quizes.getAssessmentResonNQuestion,
    getAssessmentQnRAdmin: Actions.quizes.getAssessmentResonNQuestionAdmin,
  }
)(QuizAttemptFormScreen);
